/// <amd-module name="Analytics/Boards/BurndownQueries/WorkItemIdsSnapshotQuery" />
import { FieldFilter, WorkItemFieldDescriptor } from "Analytics/Boards/BoardsQueries/DataContracts";
import { AggregationMode, ModefulValueSetting, TeamScope } from "Analytics/Boards/BoardsQueries/DataContracts";
import { WitFieldUtilities } from "Analytics/Boards/BoardsQueries/WitFieldUtilities";
import { ODataQueryOptions } from "Analytics/Common/ODataQueryOptions";
import { DateSKParser } from "Analytics/Common/Utilities/DateSKParser";
import * as PublicProjectsQueryHelper from "Analytics/Common/Utilities/PublicProjectsQueryHelper";
import { latestODataVersion } from "Analytics/Common/Utilities/QueryUtilities";
import { QueryUtilities } from "Analytics/Common/Utilities/QueryUtilities";
import { IVssPageContext } from "VSS/Platform/Context";
import { uniqueSort } from "VSS/Platform/Util/Array";
import { BurndownQueryBase } from "./BurndownQueryBase";

export interface ODataWorkItemSnapshot {
    WorkItemId: string;
}

export class WorkItemIdsSnapshotQuery extends BurndownQueryBase<string[]> {
    private static generateQueryOptions(
        pageContext: IVssPageContext,
        date: string,
        teamScopes: TeamScope[],
        filters: FieldFilter[],
        workItemFieldDescriptors: WorkItemFieldDescriptor[],
        aggregation: ModefulValueSetting<AggregationMode, string>,
        queryCompleted: boolean,
        workItemTypes: string[],
        showResolvedItemsAsCompleted = false
    ): ODataQueryOptions {
        const teams = uniqueSort(teamScopes.map(scope => scope.teamId));
        const teamsClause = QueryUtilities.isInArray("t/TeamSK", teams, false /* useQuotes */);

        const projects = uniqueSort(teamScopes.map(scope => scope.projectId));
        const projectsClause = QueryUtilities.isInArray("ProjectSK", projects, false /* useQuotes */);

        const dateSK = DateSKParser.dateStringToDateSK(date);
        const datesClause = `DateSK eq ${dateSK}`;

        const filtersClause = WitFieldUtilities.getFiltersClause(filters, workItemFieldDescriptors);

        const workItemTypesClause = QueryUtilities.isInArray("WorkItemType", workItemTypes, true /* useQuotes */);

        let $apply =
            `filter(` +
            `(${projectsClause})` +
            ` and Teams/any(t:${teamsClause})` +
            ` and (StateCategory ${queryCompleted ? "eq" : "ne"} 'Completed'`;

        if(showResolvedItemsAsCompleted){
            $apply += 
            ` ${queryCompleted ? "or" : "and"} StateCategory ${queryCompleted ? "eq" : "ne"} 'Resolved'` +
            ` ${queryCompleted ? "or" : "and"} State ${queryCompleted ? "eq" : "ne"} 'Resolved'` 
        }

        $apply +=
        `) and (RevisedDateSK eq null or RevisedDateSK gt ${dateSK})` + // Added to improve query perf
        ` and (${datesClause})` +
        ` and (${workItemTypesClause})`;

        if (filtersClause) {
            $apply += ` and (${filtersClause})`;
        }

        if (aggregation.identifier === AggregationMode.Sum) {
            const analyticsEffortFieldName = WitFieldUtilities.getFieldODataPropertyName(aggregation.settings);
            $apply += ` and ${analyticsEffortFieldName} ne null`;
        }

        $apply += ")";

        return PublicProjectsQueryHelper.forceProjectScoping(pageContext, projects, {
            entityType: "WorkItemSnapshot",
            oDataVersion: latestODataVersion,
            $apply: $apply,
            $select: "WorkItemId"
        });
    }
    constructor(
        pageContext: IVssPageContext,
        date: string,
        teamScopes: TeamScope[],
        filters: FieldFilter[],
        workItemFieldDescriptors: WorkItemFieldDescriptor[],
        aggregation: ModefulValueSetting<AggregationMode, string>,
        queryCompleted: boolean,
        workItemTypes: string[],
        showResolvedItemsAsCompleted = false,
        commandName?: string
    ) {
        super(
            WorkItemIdsSnapshotQuery.generateQueryOptions(
                pageContext,
                date,
                teamScopes,
                filters,
                workItemFieldDescriptors,
                aggregation,
                queryCompleted,
                workItemTypes,
                showResolvedItemsAsCompleted
            ), commandName
        );
    }

    public getQueryName(): string {
        return "WorkItemIdsSnapshotQuery";
    }

    protected interpretQueryResults(data: { value: ODataWorkItemSnapshot[] }): string[] {
        return data.value.map(value => value.WorkItemId);
    }
}
